import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignsApi } from '../api';
import { toast } from 'sonner';
import {
  ArrowLeft, Play, Pause, StopCircle, Download, Send, Eye, MousePointer,
  MessageSquare, AlertTriangle, Loader2, RefreshCw, ChevronLeft, ChevronRight
} from 'lucide-react';

const STATUS_MAP = {
  draft: { bg: 'bg-[#E8EAED]', text: 'text-[#5F6368]' },
  active: { bg: 'bg-[#E6F4EA]', text: 'text-[#1E8E3E]' },
  paused: { bg: 'bg-[#FEF7E0]', text: 'text-[#F9AB00]' },
  completed: { bg: 'bg-[#E8F0FE]', text: 'text-[#1A73E8]' },
  stopped: { bg: 'bg-[#FCE8E6]', text: 'text-[#D93025]' },
  pending: { bg: 'bg-[#E8EAED]', text: 'text-[#5F6368]' },
  sent: { bg: 'bg-[#E0F7FA]', text: 'text-[#0891B2]' },
  bounced: { bg: 'bg-[#FFF3E0]', text: 'text-[#EA580C]' },
  replied: { bg: 'bg-[#F3E8FF]', text: 'text-[#7C3AED]' },
  blacklisted: { bg: 'bg-[#FCE4EC]', text: 'text-[#D81B60]' },
  skipped: { bg: 'bg-[#F5F5F5]', text: 'text-[#9AA0A6]' },
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsPage, setLeadsPage] = useState(1);
  const [activity, setActivity] = useState([]);
  const [tab, setTab] = useState('leads');
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, l, a] = await Promise.all([
        campaignsApi.get(id),
        campaignsApi.getLeads(id, { page: leadsPage, limit: 50 }),
        campaignsApi.getActivity(id, 100),
      ]);
      setCampaign(c);
      setLeads(l.leads);
      setLeadsTotal(l.total);
      setActivity(a);
    } catch { toast.error('Failed to load campaign'); navigate('/outreach/campaigns'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [id, leadsPage]);

  useEffect(() => {
    if (campaign?.status !== 'active') return;
    const interval = setInterval(fetchAll, 10000);
    return () => clearInterval(interval);
  }, [campaign?.status]);

  const handleAction = async (action) => {
    try {
      if (action === 'launch') { await campaignsApi.launch(id); toast.success('Campaign launched'); }
      if (action === 'pause') { await campaignsApi.pause(id); toast.success('Campaign paused'); }
      if (action === 'resume') { await campaignsApi.resume(id); toast.success('Campaign resumed'); }
      if (action === 'stop') { await campaignsApi.stop(id); toast.success('Campaign stopped'); }
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const handleExport = async () => {
    try {
      const data = await campaignsApi.export(id);
      const headers = Object.keys(data[0] || {});
      const csv = [headers.join(','), ...data.map(r => headers.map(h => `"${r[h] || ''}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `campaign_${campaign?.name || id}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  if (loading || !campaign) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#1A73E8]" /></div>;

  const openRate = campaign.sent ? ((campaign.opened / campaign.sent) * 100).toFixed(1) : '0';
  const clickRate = campaign.sent ? ((campaign.clicked / campaign.sent) * 100).toFixed(1) : '0';
  const replyRate = campaign.sent ? ((campaign.replied / campaign.sent) * 100).toFixed(1) : '0';
  const bounceRate = campaign.sent ? ((campaign.bounced / campaign.sent) * 100).toFixed(1) : '0';
  const leadsPages = Math.ceil(leadsTotal / 50);
  const st = STATUS_MAP[campaign.status] || STATUS_MAP.draft;

  return (
    <div data-testid="campaign-detail">
      <button onClick={() => navigate('/outreach/campaigns')} className="flex items-center gap-1 text-sm text-[#5F6368] hover:text-[#202124] mb-4 transition-colors"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-[#202124] flex items-center gap-2">
            {campaign.name}
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{campaign.status}</span>
          </h1>
          <p className="text-sm text-[#5F6368]">{campaign.total_leads} leads · {campaign.sequence?.length || 0} steps</p>
        </div>
        <div className="flex gap-2">
          {campaign.status === 'draft' && <button onClick={() => handleAction('launch')} className="flex items-center gap-1.5 px-4 py-2 bg-[#1A73E8] hover:bg-[#174EA6] text-white rounded-full text-sm font-medium shadow-sm"><Play className="w-4 h-4" /> Launch</button>}
          {campaign.status === 'active' && <button onClick={() => handleAction('pause')} className="flex items-center gap-1.5 px-4 py-2 bg-[#FEF7E0] hover:bg-[#F9AB00]/20 text-[#F9AB00] rounded-full text-sm font-medium border border-[#F9AB00]/30"><Pause className="w-4 h-4" /> Pause</button>}
          {campaign.status === 'paused' && <button onClick={() => handleAction('resume')} className="flex items-center gap-1.5 px-4 py-2 bg-[#1A73E8] hover:bg-[#174EA6] text-white rounded-full text-sm font-medium shadow-sm"><Play className="w-4 h-4" /> Resume</button>}
          {['active', 'paused'].includes(campaign.status) && <button onClick={() => handleAction('stop')} className="flex items-center gap-1.5 px-4 py-2 bg-[#FCE8E6] hover:bg-[#D93025]/20 text-[#D93025] rounded-full text-sm font-medium border border-[#D93025]/30"><StopCircle className="w-4 h-4" /> Stop</button>}
          <button onClick={handleExport} className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F1F3F4] text-[#202124] rounded-full text-sm font-medium"><Download className="w-4 h-4" /> Export</button>
          <button onClick={fetchAll} className="p-2 text-[#5F6368] hover:bg-[#F1F3F4] rounded-full"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { label: 'Sent', value: campaign.sent, icon: Send, color: 'text-[#1A73E8]' },
          { label: 'Open Rate', value: `${openRate}%`, icon: Eye, color: 'text-[#1E8E3E]' },
          { label: 'Click Rate', value: `${clickRate}%`, icon: MousePointer, color: 'text-[#0891B2]' },
          { label: 'Reply Rate', value: `${replyRate}%`, icon: MessageSquare, color: 'text-[#7C3AED]' },
          { label: 'Bounce Rate', value: `${bounceRate}%`, icon: AlertTriangle, color: 'text-[#D93025]' },
          { label: 'Unsub', value: campaign.unsubscribed, icon: AlertTriangle, color: 'text-[#D81B60]' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white border border-[#E2E8F0] rounded-2xl p-4 text-center">
            <Icon className={`w-5 h-5 mx-auto mb-1 ${color}`} />
            <p className="text-lg font-bold text-[#202124]">{value}</p>
            <p className="text-xs text-[#9AA0A6] font-medium">{label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        {['leads', 'activity'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t ? 'bg-[#D3E3FD] text-[#041E49]' : 'text-[#5F6368] hover:bg-[#F1F3F4]'}`}>
            {t === 'leads' ? 'Leads' : 'Activity Feed'}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        {tab === 'leads' ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[#F1F3F4] text-left">
                  {['Company', 'Email', 'Step', 'Status', 'Last Sent', 'Opened', 'Replied'].map(h => (
                    <th key={h} className="px-5 py-3 text-xs font-semibold text-[#5F6368] uppercase tracking-wide">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {leads.map((cl) => {
                    const s = STATUS_MAP[cl.status] || STATUS_MAP.pending;
                    return (
                      <tr key={cl.id} className="border-b border-[#F1F3F4] hover:bg-[#F8F9FA] transition-colors">
                        <td className="px-5 py-3 text-[#202124] font-medium">{cl.lead?.company_name || '-'}</td>
                        <td className="px-5 py-3 text-[#5F6368]">{cl.lead?.email || '-'}</td>
                        <td className="px-5 py-3 text-[#5F6368]">{cl.current_step}/{campaign.sequence?.length || 0}</td>
                        <td className="px-5 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{cl.status}</span></td>
                        <td className="px-5 py-3 text-xs text-[#9AA0A6]">{cl.last_sent_at ? new Date(cl.last_sent_at).toLocaleString() : '-'}</td>
                        <td className="px-5 py-3 text-xs text-[#9AA0A6]">{cl.opened_at ? new Date(cl.opened_at).toLocaleString() : '-'}</td>
                        <td className="px-5 py-3 text-xs text-[#9AA0A6]">{cl.replied_at ? new Date(cl.replied_at).toLocaleString() : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {leadsPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-[#F1F3F4]">
                <span className="text-xs text-[#5F6368]">Page {leadsPage} of {leadsPages}</span>
                <div className="flex gap-1">
                  <button onClick={() => setLeadsPage(p => Math.max(1, p - 1))} disabled={leadsPage === 1} className="p-1.5 rounded-full text-[#5F6368] hover:bg-[#F1F3F4] disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => setLeadsPage(p => Math.min(leadsPages, p + 1))} disabled={leadsPage === leadsPages} className="p-1.5 rounded-full text-[#5F6368] hover:bg-[#F1F3F4] disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="divide-y divide-[#F1F3F4] max-h-[500px] overflow-y-auto">
            {activity.length === 0 ? (
              <p className="p-8 text-center text-[#9AA0A6]">No activity yet</p>
            ) : activity.map((log) => {
              const s = STATUS_MAP[log.status] || STATUS_MAP.pending;
              return (
                <div key={log.id} className="px-5 py-3 flex items-center gap-3 hover:bg-[#F8F9FA] transition-colors">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.status === 'sent' ? 'bg-[#1E8E3E]' : 'bg-[#D93025]'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#202124] truncate"><span className="font-medium">{log.company_name || log.email}</span> — Step {log.step}</p>
                    <p className="text-xs text-[#9AA0A6]">{log.subject}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{log.status}</span>
                  <span className="text-xs text-[#9AA0A6] flex-shrink-0">{new Date(log.created_at).toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
